import { useState } from 'react'
import { isPortalRateLimited } from '../shared/rateLimited'

import { formatDate } from './ParentLayout.helpers'

function money(value) {
  return `¥${Math.abs(value).toLocaleString()}`
}

// Mirror of the server projection's semester ordering KEY (routes/
// portal_billing.py sorts ascending on (start_date or "", id or "")).
// The key is faithful; the display direction is not — SemesterList
// deliberately renders newest-first so the current semester keeps the top
// position. An explicit tuple comparison, not string concatenation: a
// separator character would be invisible load-bearing syntax, and no
// separator at all mis-sorts null-date rows above dated ones.
function semesterSortTuple(row) {
  return [row?.semester?.start_date || '', row?.semester?.id || '']
}

function tupleGreater(a, b) {
  return a[0] > b[0] || (a[0] === b[0] && a[1] > b[1])
}

// Fail-closed collapse rule: a row a parent must see is never collapsible —
// unavailable evidence, money owed in either direction, or a lifecycle mode
// this rule does not know. Only older, available, fully-settled rows of the
// KNOWN modes may hide behind the toggle.
function rowIsMaterial(row) {
  if (!row.available) return true
  if (row.lifecycleMode === 'credits') return row.creditBalance !== 0
  if (row.lifecycleMode === 'regular' || row.lifecycleMode === 'trial') {
    return row.familyOwes !== 0 || row.companyOwes !== 0
  }
  return true
}

function BillingUnavailable({ t, error }) {
  // #366: a throttled billing read (the admin boundary's tightest bucket) is
  // temporary — calm wait copy, never "data unavailable".
  if (isPortalRateLimited(error)) {
    return (
      <div className="portal-degraded-banner" role="status">
        <div className="portal-degraded-banner-title">{t('rateLimitedTitle')}</div>
        <div className="portal-degraded-banner-body">{t('rateLimited')}</div>
      </div>
    )
  }
  return (
    <div className="portal-degraded-banner" role="alert">
      <div className="portal-degraded-banner-title">{t('dataUnavailableTitle')}</div>
      <div className="portal-degraded-banner-body">{t('dataUnavailableBody')}</div>
    </div>
  )
}

function MoneyCard({ label, value, className = '' }) {
  return (
    <div className={`billing-summary-card ${className}`.trim()}>
      <span className="billing-summary-label">{label}</span>
      <span className="billing-summary-value">{money(value)}</span>
    </div>
  )
}

function PaymentHistory({ payments, t }) {
  return (
    <details className="billing-payment-details">
      <summary className="collapsible-toggle">{t('paymentHistory')}</summary>
      {payments.length > 0 ? (
        <div className="payment-history-list">
          {payments.map(payment => (
            <div key={payment.id} className="payment-history-row">
              <span className="payment-date">{formatDate(payment.date)}</span>
              <span className="payment-amount">{money(payment.amount)}</span>
              {payment.note && <span className="payment-note">{payment.note}</span>}
            </div>
          ))}
        </div>
      ) : (
        <div className="portal-section-empty">{t('noPayments')}</div>
      )}
    </details>
  )
}

function RolloverHistory({ rollovers = [], t }) {
  if (rollovers.length === 0) return null
  return (
    <details className="billing-rollover-details">
      <summary className="collapsible-toggle">{t('rolloverHistory')}</summary>
      <div className="rollover-history-list">
        {rollovers.map(rollover => (
          <div
            key={`${rollover.id}-${rollover.direction}`}
            className="rollover-history-row"
            data-billing-rollover={rollover.id}
          >
            <div className="rollover-history-heading">
              <span className="rollover-direction">
                {t(rollover.direction === 'in' ? 'rolloverDirectionIn' : 'rolloverDirectionOut')}
              </span>
              <span className="rollover-amount">{money(rollover.amount)}</span>
            </div>
            <div className="rollover-semesters">
              {rollover.sourceSemester.name || rollover.sourceSemester.id}
              {' → '}
              {rollover.targetSemester.name || rollover.targetSemester.id}
            </div>
            <div className="rollover-history-meta">
              <span>{formatDate(rollover.date)}</span>
              <span className="rollover-id">{rollover.id}</span>
            </div>
            {rollover.note && <div className="rollover-note">{rollover.note}</div>}
          </div>
        ))}
      </div>
    </details>
  )
}

function CreditSemester({ row, t }) {
  const deficit = row.creditBalance < 0
  const stats = [
    { key: 'purchased', label: t('totalPurchased'), value: row.totalPurchased },
    { key: 'consumed', label: t('totalConsumed'), value: row.totalConsumed },
    { key: 'semester-consumed', label: t('semesterConsumed'), value: row.semesterConsumed },
  ]

  return (
    <>
      <div className="billing-balance-hero">
        <span className="billing-balance-label">
          {deficit ? t('creditDeficit') : t('availableCredit')}
        </span>
        <span className={`billing-balance-value ${deficit ? 'billing-cell-warning' : 'billing-cell-green'}`}>
          {money(row.creditBalance)}
        </span>
      </div>
      <div className="billing-summary-grid">
        {stats.map(stat => (
          <MoneyCard key={stat.key} label={stat.label} value={stat.value} />
        ))}
      </div>
    </>
  )
}

function SemesterBillingRow({ row, t }) {
  const semesterName = row.semester.name || row.semester.id
  if (!row.available) {
    return (
      <div className="billing-semester-row" data-billing-semester={row.semester.id}>
        <div className="billing-semester-header">{semesterName}</div>
        <BillingUnavailable t={t} />
      </div>
    )
  }

  if (row.lifecycleMode === 'credits') {
    return (
      <div className="billing-semester-row" data-billing-semester={row.semester.id}>
        <div className="billing-semester-header">{semesterName}</div>
        <CreditSemester row={row} t={t} />
      </div>
    )
  }

  const stats = [
    { key: 'gross', label: t('grossContract'), value: row.grossContract },
    { key: 'net', label: t('netContract'), value: row.netContract },
    { key: 'cash', label: t('cashReceived'), value: row.cashReceived },
    { key: 'refunds', label: t('cashRefunded'), value: row.cashRefunded },
    { key: 'discounts', label: t('discounts'), value: row.discounts },
    { key: 'rollover-in', label: t('rolloverIn'), value: row.rolloverIn },
    { key: 'rollover-out', label: t('rolloverOut'), value: row.rolloverOut },
    { key: 'settlement', label: t('settlementDue'), value: row.settlementDue },
  ]

  return (
    <div className="billing-semester-row" data-billing-semester={row.semester.id}>
      <div className="billing-semester-header">{semesterName}</div>
      <div className="billing-summary-grid billing-obligation-grid">
        <MoneyCard label={t('familyOwes')} value={row.familyOwes} />
        <MoneyCard label={t('companyOwes')} value={row.companyOwes} />
      </div>
      <div className="billing-summary-grid">
        {stats.map(stat => (
          <MoneyCard key={stat.key} label={stat.label} value={stat.value} />
        ))}
      </div>
      <PaymentHistory payments={row.payments} t={t} />
      <RolloverHistory rollovers={row.rollovers} t={t} />
    </div>
  )
}

function SemesterList({ rows, totalsAvailable, showEarlier, onToggle, t }) {
  // Presentation order is newest-first EVERYWHERE: the current semester keeps
  // the top position and expanding loads earlier semesters below it — the
  // toggle must never reshuffle what the parent is already looking at.
  const displayRows = [...rows].sort((a, b) => {
    const ta = semesterSortTuple(a)
    const tb = semesterSortTuple(b)
    if (tupleGreater(ta, tb)) return -1
    if (tupleGreater(tb, ta)) return 1
    return 0
  })

  // Without an always-visible aggregate there is nothing keeping collapsed
  // rows "accounted for on screen", so the inconsistent state (totals null)
  // renders every row — collapse is a convenience reserved for the healthy
  // canonical state.
  if (!totalsAvailable) {
    return displayRows.map(row => (
      <SemesterBillingRow key={row.semester.id} row={row} t={t} />
    ))
  }

  const newestTuple = displayRows.length
    ? semesterSortTuple(displayRows[0])
    : null
  const pinned = row => {
    if (rowIsMaterial(row)) return true
    const tuple = semesterSortTuple(row)
    return tuple[0] === newestTuple[0] && tuple[1] === newestTuple[1]
  }
  const hiddenCount = displayRows.filter(row => !pinned(row)).length
  const visible = showEarlier ? displayRows : displayRows.filter(pinned)

  return (
    <>
      {visible.map(row => (
        <SemesterBillingRow key={row.semester.id} row={row} t={t} />
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="collapsible-toggle billing-earlier-toggle"
          aria-expanded={showEarlier}
          onClick={onToggle}
        >
          {showEarlier
            ? t('hideEarlierSemesters')
            : `${t('showEarlierSemesters')} (${hiddenCount})`}
        </button>
      )}
    </>
  )
}

export default function BillingSection({ billing, t }) {
  const loadError = billing?.error
  const billData = billing && Object.hasOwn(billing, 'data') ? billing.data : billing
  const [showEarlier, setShowEarlier] = useState(false)

  if (loadError) {
    return (
      <div className="portal-section">
        <div className="portal-section-card portal-card-teal">
          <div className="portal-section-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
            {t('billing')}
          </div>
          <BillingUnavailable t={t} error={loadError} />
        </div>
      </div>
    )
  }

  if (!billData) {
    return (
      <div className="portal-section">
        <div className="portal-section-card portal-card-teal">
          <div className="portal-section-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
            {t('billing')}
          </div>
          <div className="portal-section-empty">{t('noData')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-teal">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
          {t('billing')}
        </div>
        {billData.totals ? (
          <div className="billing-summary-grid billing-obligation-totals">
            <MoneyCard label={t('familyOwes')} value={billData.totals.familyOwes} />
            <MoneyCard label={t('companyOwes')} value={billData.totals.companyOwes} />
          </div>
        ) : (
          <BillingUnavailable t={t} />
        )}
        <div className="portal-section-sublabel">{t('semesterBreakdown')}</div>
        <div className="billing-semester-list">
          <SemesterList
            rows={billData.rows}
            totalsAvailable={Boolean(billData.totals)}
            showEarlier={showEarlier}
            onToggle={() => setShowEarlier(prev => !prev)}
            t={t}
          />
        </div>
      </div>
    </div>
  )
}
